import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  RefreshCw, Maximize, Minimize, ZoomIn, ZoomOut, ChevronRight, ChevronDown,
  ArrowRight, Plus, Trash2, X, Save,
  MessageSquare, Brain, Mail, Bell, Phone, Globe, Edit, User, Bot, Zap,
  Sparkles, Loader2, ChevronUp, Settings, Crosshair, Layers, Map,
  Search, Play, Pause, SkipForward, SkipBack, UserSearch
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${getApiUrl()}/api`;

const af = (url, opts = {}) => {
  const token = localStorage.getItem('auth_token');
  const tenantId = localStorage.getItem('active_tenant_id');
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
      ...(opts.headers || {}),
    },
  });
};

const typeBadge = (type) => {
  if (type === 'auto') return { cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', label: 'auto' };
  if (type === 'semi_auto') return { cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', label: 'semi' };
  return { cls: 'bg-slate-700/50 text-slate-500 border-slate-600/30', label: 'manuel' };
};

const toolIcon = (type) => {
  const map = {
    agent_ia: <Brain className="w-3.5 h-3.5 text-purple-400" />,
    whatsapp_baileys: <MessageSquare className="w-3.5 h-3.5 text-green-400" />,
    sms_ringover: <Phone className="w-3.5 h-3.5 text-blue-400" />,
    appel_ringover: <Phone className="w-3.5 h-3.5 text-cyan-400" />,
    email: <Mail className="w-3.5 h-3.5 text-emerald-400" />,
    notification: <Bell className="w-3.5 h-3.5 text-amber-400" />,
    humain: <User className="w-3.5 h-3.5 text-orange-400" />,
    action_crm: <Edit className="w-3.5 h-3.5 text-pink-400" />,
    webhook: <Globe className="w-3.5 h-3.5 text-slate-400" />,
  };
  return map[type] || <Zap className="w-3.5 h-3.5 text-slate-400" />;
};

const collectAllIds = (stageId, workflows, visited = new Set()) => {
  if (visited.has(stageId)) return visited;
  visited.add(stageId);
  const exits = (workflows[stageId]?.exit_triggers || []).filter(e => e.enabled !== false);
  exits.forEach(ex => collectAllIds(ex.target_stage, workflows, visited));
  return visited;
};

// ════════════════════════════════════════════════════
// DAG CHART — each node rendered ONCE with SVG edges
// ════════════════════════════════════════════════════
import dagre from 'dagre';
import getApiUrl from '../../utils/getApiUrl';

const NODE_W = 140;
const NODE_H = 56;

// Edge color classification
const HAPPY_PATH = new Set([
  // Avant R1
  'lead_r1_pris', 'confirmer_rdv', 'confirmer_rdv_j2', 'confirmer_rdv_j1',
  'rdv_confirme', 'r1_audit', 'lead_cq_a_faire', 'cq_telepro_en_cours',
  'cq_ok_transfert_amo',
  // AMO
  'amo_contact_client_24h', 'amo_rdv_vt_pris', 'vt_amo_realisee',
  // R2 Archi
  'r2_a_planifier', 'r2_planifie', 'r2_realise',
  // Post-R2
  'devis_anah_valide', 'materiaux_valides', 'materiaux_valides_sur_place',
  'octroi_anah_recu',
  // Justine Négo
  'sonder_client', 'nego_client_avance_solde',
  'client_30_comptant', 'anah_30_avance_a_faire', 'anah_30_demande_amo', 'anah_30_en_cours',
  'avance_recue_sani', 'accord_chantier',
  // Chantier
  'chantier_a_demarrer', 'revalidation_client',
  'date_debut_planifiee', 'commandes_passees', 'sous_traitants_mandates',
  'chantier_en_cours', 'chantier_termine',
  // Post-chantier
  'chantier_valide',
  'factures_deposees_client', 'signature_plan_financement',
  'plan_signe_renvoye_amo', 'dossier_finalise',
  // Compta
  'facture_devis_anah', 'toutes_factures_emises',
  // AMO Financement
  'demande_plan_financement', 'plan_financement_recu',
  // Paiements
  'paiement_st_a_faire', 'paiement_st_effectue',
  'paiement_anah_solde_a_recevoir', 'paiement_anah_solde_recu'
]);
const LOST_PATH = new Set([
  'lead_perdu_avant_r1', 'lead_perdu_nrp', 'lead_perdu_suite_r1',
  'annule_par_societe', 'lead_annule_par_client', 'cq_client_non_interesse',
  'lead_hors_zone', 'lead_hors_mpa', 'r2_refus',
  'reserves_constatees', 'reserves_st_informe', 'levee_reserves'
]);

const getEdgeColor = (targetId) => {
  if (HAPPY_PATH.has(targetId)) return { stroke: '#22c55e', opacity: 0.6, marker: 'arrow-green' };
  if (LOST_PATH.has(targetId)) return { stroke: '#ef4444', opacity: 0.5, marker: 'arrow-red' };
  return { stroke: '#f59e0b', opacity: 0.55, marker: 'arrow-amber' }; // intermediate
};

const SIBLING_ROOT = 'lead_hors_zone'; // same level as lead, fed by sources
// Qualification exits: positioned to the RIGHT of lead
const QUALIFICATION_EXITS = new Set(['lead_hors_mpa', 'lead_pac_iso_interesse', 'lead_pac_iso_a_recontacter', 'lead_monte_escalier']);

const buildDagLayout = (rootId, stagesById, workflows, allStageIds = []) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', ranksep: 70, nodesep: 30, marginx: 30, marginy: 30 });
  g.setDefaultEdgeLabel(() => ({}));

  // BFS to find all reachable nodes from root
  const reachable = new Set();
  const queue = [rootId];
  while (queue.length) {
    const id = queue.shift();
    if (reachable.has(id) || !stagesById[id]) continue;
    reachable.add(id);
    const exits = (workflows[id]?.exit_triggers || []).filter(e => e.enabled !== false);
    exits.forEach(ex => { if (stagesById[ex.target_stage]) queue.push(ex.target_stage); });
  }

  // Also add sibling root (lead_hors_zone) at same rank as lead
  if (stagesById[SIBLING_ROOT] && !reachable.has(SIBLING_ROOT)) {
    reachable.add(SIBLING_ROOT);
  }

  // Add nodes
  reachable.forEach(id => { g.setNode(id, { width: NODE_W, height: NODE_H }); });

  // Add edges (deduplicate)
  const edgeSet = new Set();
  const edgeMeta = [];
  reachable.forEach(id => {
    if (id === SIBLING_ROOT) return; // no outgoing edges from sibling root
    const exits = (workflows[id]?.exit_triggers || []).filter(e => e.enabled !== false);
    exits.forEach(ex => {
      const target = ex.target_stage;
      if (!reachable.has(target)) return;
      const key = `${id}→${target}`;
      if (!edgeSet.has(key)) {
        edgeSet.add(key);
        g.setEdge(id, target);
        edgeMeta.push({ from: id, to: target, type: ex.type });
      }
    });
  });

  dagre.layout(g);
  const nodes = {};
  g.nodes().forEach(id => { nodes[id] = g.node(id); });

  // Force lead_hors_zone to same Y as lead and to its left
  if (nodes[rootId] && nodes[SIBLING_ROOT]) {
    nodes[SIBLING_ROOT].y = nodes[rootId].y;
    nodes[SIBLING_ROOT].x = nodes[rootId].x - NODE_W - 60;
  }

  // Force qualification exits to the FAR RIGHT, stacked vertically, clear of the main flow
  if (nodes[rootId]) {
    const qualExits = [...QUALIFICATION_EXITS].filter(id => nodes[id]);
    if (qualExits.length > 0) {
      // Find the rightmost node in the entire graph (excluding qualification exits themselves)
      let maxX = -Infinity;
      Object.entries(nodes).forEach(([id, n]) => {
        if (!QUALIFICATION_EXITS.has(id)) maxX = Math.max(maxX, n.x + n.width / 2);
      });
      const baseX = maxX + NODE_W + 40;
      const baseY = nodes[rootId].y;
      qualExits.forEach((id, i) => {
        nodes[id].x = baseX;
        nodes[id].y = baseY + i * (NODE_H + 18);
      });
    }
  }

  // Compute orphan nodes (not reachable from root, not sibling root)
  const orphanIds = allStageIds.filter(id => !reachable.has(id) && stagesById[id]);
  const orphanNodes = {};
  const orphanCategories = {}; // { category: [id, ...] }
  if (orphanIds.length > 0) {
    // Group orphans by category
    orphanIds.forEach(id => {
      const cat = stagesById[id]?.categorie || 'sans_categorie';
      if (!orphanCategories[cat]) orphanCategories[cat] = [];
      orphanCategories[cat].push(id);
    });

    // Position orphans on the LEFT side, grouped by category vertically
    let minX = Infinity;
    Object.values(nodes).forEach(n => { minX = Math.min(minX, n.x - n.width / 2); });
    const orphanX = minX - NODE_W - 100; // Left of the main graph

    // Category order
    const catOrder = ['avant_r1', 'apres_r1', 'final', 'aides_comp', 'sans_categorie'];
    const sortedCats = Object.keys(orphanCategories).sort((a, b) => {
      const ai = catOrder.indexOf(a); const bi = catOrder.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    let curY = (nodes[rootId]?.y || 0) - NODE_H;
    sortedCats.forEach(cat => {
      curY += 35; // space for category header
      orphanCategories[cat].forEach(id => {
        orphanNodes[id] = { x: orphanX, y: curY, width: NODE_W, height: NODE_H, category: cat };
        curY += NODE_H + 14;
      });
      curY += 10; // gap between categories
    });
  }

  return { nodes, edges: edgeMeta, reachable, orphanNodes, orphanCategories };
};

// ════════════════════════════════════════════════════
// CLIENT SEARCH BAR
// ════════════════════════════════════════════════════
const ClientSearchBar = ({ onSelectClient, activeClientId, onClear }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await af(`${API}/pipeline-workflow/client-search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setResults(data.results || []);
      setOpen(true);
    } catch { setResults([]); }
    setSearching(false);
  }, []);

  const handleChange = (val) => {
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="client-search-bar">
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all ${activeClientId ? 'bg-teal-500/15 border-teal-500/40' : 'bg-slate-800/80 border-slate-700/50 hover:border-slate-600'}`}>
        <Search className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
        <input
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Chercher un client..."
          className="bg-transparent text-[11px] text-white placeholder:text-slate-600 outline-none w-36"
          data-testid="client-search-input"
        />
        {searching && <Loader2 className="w-3 h-3 text-slate-500 animate-spin" />}
        {activeClientId && (
          <button onClick={() => { setQuery(''); setResults([]); onClear(); }} className="p-0.5 hover:bg-slate-700 rounded text-slate-500 hover:text-white" data-testid="client-search-clear">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 z-50 w-72 max-h-56 overflow-auto bg-slate-900 border border-slate-600 rounded-xl shadow-2xl p-1" data-testid="client-search-results">
          {results.map((r) => (
            <button key={r.client_id} data-testid={`client-result-${r.client_id}`}
              onClick={() => { onSelectClient(r); setOpen(false); setQuery(`${r.prenom} ${r.nom}`); }}
              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors hover:bg-slate-800 ${activeClientId === r.client_id ? 'bg-teal-500/10 ring-1 ring-teal-500/30' : ''}`}>
              <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                <User className="w-3 h-3 text-teal-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-white font-medium truncate">{r.prenom} {r.nom}</div>
                <div className="text-[9px] text-slate-500 truncate">{r.telephone} — {r.etape_nom}</div>
              </div>
              <div className="text-[8px] text-teal-400/70 font-mono flex-shrink-0">{r.source?.substring(0, 12)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════
// JOURNEY TIMELINE (bottom panel)
// ════════════════════════════════════════════════════
const JourneyTimeline = ({ journey, clientName, onClose, onStepHighlight, playingStep }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const intervalRef = useRef(null);

  const steps = journey?.journey || [];

  const play = () => {
    if (steps.length === 0) return;
    setIsPlaying(true);
    setCurrentStep(0);
    onStepHighlight?.(0);
    let idx = 0;
    intervalRef.current = setInterval(() => {
      idx++;
      if (idx >= steps.length) {
        clearInterval(intervalRef.current);
        setIsPlaying(false);
        return;
      }
      setCurrentStep(idx);
      onStepHighlight?.(idx);
    }, 1500);
  };

  const pause = () => {
    clearInterval(intervalRef.current);
    setIsPlaying(false);
  };

  const goTo = (idx) => {
    pause();
    setCurrentStep(idx);
    onStepHighlight?.(idx);
  };

  const prev = () => { if (currentStep > 0) goTo(currentStep - 1); };
  const next = () => { if (currentStep < steps.length - 1) goTo(currentStep + 1); };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  if (!journey || steps.length === 0) return null;

  return (
    <div className="bg-slate-900/90 border border-teal-500/30 rounded-xl overflow-hidden" data-testid="journey-timeline">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700/30 bg-teal-500/5">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-teal-500/20 flex items-center justify-center">
            <UserSearch className="w-3.5 h-3.5 text-teal-400" />
          </div>
          <div>
            <div className="text-[11px] font-bold text-teal-300">{clientName}</div>
            <div className="text-[9px] text-slate-500">{journey.source} — {steps.length} étapes — Étape actuelle: {journey.current_stage}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Play controls */}
          <button onClick={prev} disabled={currentStep <= 0} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white disabled:opacity-30 transition-colors" data-testid="journey-prev">
            <SkipBack className="w-3.5 h-3.5" />
          </button>
          {isPlaying ? (
            <button onClick={pause} className="p-1.5 bg-teal-600 hover:bg-teal-500 rounded-lg text-white transition-colors" data-testid="journey-pause">
              <Pause className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button onClick={play} className="p-1.5 bg-teal-600 hover:bg-teal-500 rounded-lg text-white transition-colors" data-testid="journey-play">
              <Play className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={next} disabled={currentStep >= steps.length - 1} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white disabled:opacity-30 transition-colors" data-testid="journey-next">
            <SkipForward className="w-3.5 h-3.5" />
          </button>
          <div className="w-px h-4 bg-slate-700/50 mx-1" />
          <button onClick={onClose} className="p-1 hover:bg-slate-700 rounded text-slate-500 hover:text-white transition-colors" data-testid="journey-close">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {/* Timeline steps */}
      <div className="px-4 py-3 overflow-x-auto">
        <div className="flex items-center gap-0 min-w-max">
          {steps.map((step, i) => {
            const isActive = i <= currentStep;
            const isCurrent = i === currentStep;
            const isLast = step.is_current;
            return (
              <React.Fragment key={i}>
                <button
                  onClick={() => goTo(i)}
                  className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-all cursor-pointer min-w-[90px] ${isCurrent ? 'bg-teal-500/20 ring-1 ring-teal-400/50 scale-105' : isActive ? 'bg-slate-800/60' : 'opacity-40 hover:opacity-70'}`}
                  data-testid={`journey-step-${i}`}
                >
                  <div className={`w-3 h-3 rounded-full border-2 transition-all ${isCurrent ? 'bg-teal-400 border-teal-300 shadow-lg shadow-teal-400/30' : isActive ? 'bg-teal-500/60 border-teal-500/50' : 'bg-slate-700 border-slate-600'} ${isLast ? 'ring-2 ring-teal-400/30' : ''}`} />
                  <span className={`text-[10px] font-semibold leading-tight text-center ${isCurrent ? 'text-teal-300' : isActive ? 'text-white' : 'text-slate-500'}`}>{step.stage_name}</span>
                  <span className={`text-[8px] ${isCurrent ? 'text-teal-400' : 'text-slate-600'}`}>{step.date_display}</span>
                  {step.days_since_prev !== undefined && (
                    <span className="text-[7px] text-amber-500/70">+{step.days_since_prev}j</span>
                  )}
                </button>
                {i < steps.length - 1 && (
                  <div className={`w-8 h-0.5 flex-shrink-0 rounded-full transition-all ${i < currentStep ? 'bg-teal-500/50' : 'bg-slate-700/50'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};



const DagChart = ({ rootId, stagesById, workflows, leadCounts, selectedStage, onSelect, leadSources, phaseFilter, traceData, onSourceClick, onTraceNodeClick, allStageIds, journeyHighlight, savedPositions, onPositionsChange, frozen, categoryConfig }) => {
  const containerRef = useRef(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [panning, setPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const [showMinimap, setShowMinimap] = useState(true);

  // Node drag state
  const [dragNodeId, setDragNodeId] = useState(null);
  const [dragGroup, setDragGroup] = useState(null); // 'avant_r1' | 'apres_r1' | null
  const dragNodeStart = useRef({ mx: 0, my: 0, origPositions: {} });
  const [localPositions, setLocalPositions] = useState({}); // overrides for dragged nodes
  const [hasUnsaved, setHasUnsaved] = useState(false);
  
  // Multi-select state
  const [selectedNodeIds, setSelectedNodeIds] = useState(new Set());
  const [selectionRect, setSelectionRect] = useState(null); // { x1, y1, x2, y2 } in screen coords
  const selectionStart = useRef(null);

  const { nodes: dagreNodes, edges, orphanNodes, orphanCategories } = useMemo(
    () => buildDagLayout(rootId, stagesById, workflows, allStageIds || []),
    [rootId, stagesById, workflows, allStageIds]
  );

  // Merge dagre nodes with saved positions and local overrides
  const nodes = useMemo(() => {
    const merged = {};
    Object.entries(dagreNodes).forEach(([id, n]) => {
      const saved = savedPositions?.[id];
      const local = localPositions[id];
      merged[id] = {
        ...n,
        x: local?.x ?? saved?.x ?? n.x,
        y: local?.y ?? saved?.y ?? n.y,
      };
    });
    return merged;
  }, [dagreNodes, savedPositions, localPositions]);

  // Filter nodes by phase
  const filteredNodeIds = useMemo(() => {
    if (!phaseFilter) return new Set(Object.keys(nodes));
    const ids = new Set();
    Object.keys(nodes).forEach(id => {
      const cat = stagesById[id]?.categorie || '';
      if (cat === phaseFilter || cat === 'final') ids.add(id);
    });
    return ids;
  }, [phaseFilter, nodes, stagesById]);

  // Wheel zoom — only on pinch/ctrl+wheel, normal scroll = pan
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const factor = e.deltaY > 0 ? 0.98 : 1.02;
      setScale(s => {
        const newScale = Math.max(0.15, Math.min(3, s * factor));
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const mx = e.clientX - rect.left;
          const my = e.clientY - rect.top;
          setPan(p => ({
            x: mx - (mx - p.x) * (newScale / s),
            y: my - (my - p.y) * (newScale / s)
          }));
        }
        return newScale;
      });
    } else {
      setPan(p => ({
        x: p.x - e.deltaX,
        y: p.y - e.deltaY
      }));
    }
  }, []);

  // Attach wheel handler with passive:false for preventDefault
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const visibleNodes = phaseFilter ? Object.fromEntries(Object.entries(nodes).filter(([id]) => filteredNodeIds.has(id))) : nodes;
  const visibleEdges = phaseFilter ? edges.filter(e => filteredNodeIds.has(e.from) && filteredNodeIds.has(e.to)) : edges;

  if (Object.keys(visibleNodes).length === 0) return null;

  // Compute content bounds — include source nodes and orphan nodes positions
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  Object.values(visibleNodes).forEach(n => {
    minX = Math.min(minX, n.x - n.width / 2);
    minY = Math.min(minY, n.y - n.height / 2);
    maxX = Math.max(maxX, n.x + n.width / 2);
    maxY = Math.max(maxY, n.y + n.height / 2);
  });
  // Include orphan nodes in bounds (only when no phase filter)
  const visibleOrphansRaw = (!phaseFilter) ? orphanNodes : {};
  // Orphans use computed layout (left column by category) — only local drag overrides apply
  const visibleOrphans = {};
  Object.entries(visibleOrphansRaw).forEach(([id, n]) => {
    const local = localPositions[id];
    visibleOrphans[id] = { ...n, x: local?.x ?? n.x, y: local?.y ?? n.y };
  });
  Object.values(visibleOrphans).forEach(n => {
    minX = Math.min(minX, n.x - n.width / 2);
    minY = Math.min(minY, n.y - n.height / 2);
    maxX = Math.max(maxX, n.x + n.width / 2);
    maxY = Math.max(maxY, n.y + n.height / 2);
  });

  // SocialAC nodes positions — REMOVED: AC stages are now real pipeline stages
  const socialacNodes = null;

  // Content bounds finalized

  const sourcePadTop = leadSources.length > 0 && !phaseFilter ? 90 : 0;
  const pad = 40;

  // Pre-compute source positions to include them in bounds
  const rootNode = visibleNodes[rootId];
  const siblingNode = visibleNodes[SIBLING_ROOT];
  const sourcesCenterX = (rootNode && siblingNode)
    ? (rootNode.x + siblingNode.x) / 2
    : (rootNode?.x || 0);
  const tmpSources = (!phaseFilter && rootNode) ? leadSources.map((src, i) => {
    const totalW = leadSources.length * 110;
    const startX = sourcesCenterX - totalW / 2 + 55;
    return { ...src, x: startX + i * 110, y: (rootNode?.y || 0) - NODE_H / 2 - 70 };
  }) : [];

  // Extend bounds to include source nodes
  tmpSources.forEach(src => {
    minX = Math.min(minX, src.x - 60);
    maxX = Math.max(maxX, src.x + 60);
    minY = Math.min(minY, src.y - 25);
  });

  const contentW = maxX - minX + pad * 2;
  const contentH = maxY - minY + pad * 2;
  const offX = -minX + pad;
  const offY = -minY + pad;

  // Final source positions with correct offsets
  const sourceNodes = tmpSources;

  // Pan handlers (only when not dragging a node)
  const handleMouseDown = (e) => {
    if (e.button !== 0 || dragNodeId) return;
    // Shift+click on canvas = start selection rectangle
    if (e.shiftKey && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      selectionStart.current = { sx, sy };
      setSelectionRect({ x1: sx, y1: sy, x2: sx, y2: sy });
      return;
    }
    // Click on canvas without shift = clear selection + pan
    setSelectedNodeIds(new Set());
    setPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const handleMouseMove = (e) => {
    // Selection rectangle drag
    if (selectionStart.current && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setSelectionRect(prev => prev ? {
        ...prev,
        x2: e.clientX - rect.left,
        y2: e.clientY - rect.top
      } : null);
      return;
    }
    // Node drag (single, group, or multi-selected)
    if (dragNodeId) {
      const dx = (e.clientX - dragNodeStart.current.mx) / scale;
      const dy = (e.clientY - dragNodeStart.current.my) / scale;
      const origPositions = dragNodeStart.current.origPositions;
      const newPositions = { ...localPositions };
      Object.entries(origPositions).forEach(([id, orig]) => {
        newPositions[id] = { x: orig.x + dx, y: orig.y + dy };
      });
      setLocalPositions(newPositions);
      setHasUnsaved(true);
      return;
    }
    // Canvas pan
    if (!panning) return;
    setPan({
      x: panStart.current.panX + (e.clientX - panStart.current.x),
      y: panStart.current.panY + (e.clientY - panStart.current.y)
    });
  };
  const handleMouseUp = () => {
    // Finish selection rectangle
    if (selectionStart.current && selectionRect && containerRef.current) {
      const allNodes = { ...nodes, ...(visibleOrphans || {}) };
      const rx1 = Math.min(selectionRect.x1, selectionRect.x2);
      const ry1 = Math.min(selectionRect.y1, selectionRect.y2);
      const rx2 = Math.max(selectionRect.x1, selectionRect.x2);
      const ry2 = Math.max(selectionRect.y1, selectionRect.y2);
      const selected = new Set();
      Object.entries(allNodes).forEach(([id, n]) => {
        // Convert node position to screen coords
        const screenX = n.x * scale + pan.x;
        const screenY = n.y * scale + pan.y;
        if (screenX >= rx1 && screenX <= rx2 && screenY >= ry1 && screenY <= ry2) {
          selected.add(id);
        }
      });
      setSelectedNodeIds(selected);
      selectionStart.current = null;
      setSelectionRect(null);
      return;
    }
    setPanning(false);
    if (dragNodeId) {
      setDragNodeId(null);
      setDragGroup(null);
    }
  };

  // Start dragging a single node (or all selected if part of selection)
  const startNodeDrag = (e, nodeId) => {
    e.stopPropagation();
    e.preventDefault();
    const allNodes = { ...nodes, ...(visibleOrphans || {}) };
    const node = allNodes[nodeId];
    if (!node) return;
    
    // If this node is in the selection, drag all selected nodes
    if (selectedNodeIds.has(nodeId) && selectedNodeIds.size > 1) {
      const origPositions = {};
      selectedNodeIds.forEach(id => {
        const n = allNodes[id];
        if (n) origPositions[id] = { x: n.x, y: n.y };
      });
      setDragNodeId(nodeId);
      setDragGroup('selection');
      dragNodeStart.current = { mx: e.clientX, my: e.clientY, origPositions };
    } else {
      // Single node drag (also toggle selection with Shift)
      if (e.shiftKey) {
        setSelectedNodeIds(prev => {
          const next = new Set(prev);
          if (next.has(nodeId)) next.delete(nodeId);
          else next.add(nodeId);
          return next;
        });
        return;
      }
      setDragNodeId(nodeId);
      setDragGroup(null);
      dragNodeStart.current = {
        mx: e.clientX,
        my: e.clientY,
        origPositions: { [nodeId]: { x: node.x, y: node.y } }
      };
    }
  };

  // Start dragging a group (avant_r1 or apres_r1)
  const startGroupDrag = (e, group) => {
    e.stopPropagation();
    e.preventDefault();
    const origPositions = {};
    Object.entries(nodes).forEach(([id, n]) => {
      const cat = stagesById[id]?.categorie || '';
      if (group === 'avant_r1' && (cat === 'avant_r1')) {
        origPositions[id] = { x: n.x, y: n.y };
      } else if (group === 'apres_r1' && (cat === 'apres_r1')) {
        origPositions[id] = { x: n.x, y: n.y };
      }
    });
    if (Object.keys(origPositions).length === 0) return;
    const firstId = Object.keys(origPositions)[0];
    setDragNodeId(firstId);
    setDragGroup(group);
    dragNodeStart.current = { mx: e.clientX, my: e.clientY, origPositions };
  };

  const fitToScreen = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const scaleX = rect.width / contentW;
    const scaleY = rect.height / contentH;
    const newScale = Math.min(scaleX, scaleY, 1.2) * 0.9;
    setScale(newScale);
    setPan({
      x: (rect.width - contentW * newScale) / 2,
      y: (rect.height - contentH * newScale) / 2
    });
  };

  // Center on a node
  const centerOn = (nodeId) => {
    if (!containerRef.current) return;
    const n = visibleNodes[nodeId] || visibleOrphans[nodeId];
    if (!n) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPan({
      x: rect.width / 2 - (n.x + offX) * scale,
      y: rect.height / 2 - (n.y + offY) * scale
    });
  };

  // Get all current node positions (merged dagre + saved + local)
  const getAllCurrentPositions = () => {
    const positions = {};
    Object.entries(nodes).forEach(([id, n]) => { positions[id] = { x: n.x, y: n.y }; });
    Object.entries(orphanNodes).forEach(([id, n]) => { positions[id] = { x: n.x, y: n.y }; });
    return positions;
  };


  return (
    <div className="relative w-full h-full">
      {/* Main canvas */}
      <div ref={containerRef} className="w-full h-full overflow-hidden"
        style={{ cursor: panning || dragNodeId ? 'grabbing' : 'grab' }}
        onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
        data-testid="dag-chart">

        {/* Selection rectangle */}
        {selectionRect && (
          <div
            className="absolute border-2 border-amber-400/60 bg-amber-400/10 rounded pointer-events-none z-50"
            style={{
              left: Math.min(selectionRect.x1, selectionRect.x2),
              top: Math.min(selectionRect.y1, selectionRect.y2),
              width: Math.abs(selectionRect.x2 - selectionRect.x1),
              height: Math.abs(selectionRect.y2 - selectionRect.y1),
            }}
          />
        )}

        {/* Selection count badge */}
        {selectedNodeIds.size > 0 && !selectionRect && (
          <div className="absolute top-2 left-2 z-50 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-lg text-xs text-amber-400 flex items-center gap-2">
            <span className="font-bold">{selectedNodeIds.size} sélectionné{selectedNodeIds.size > 1 ? 's' : ''}</span>
            <button onClick={() => setSelectedNodeIds(new Set())} className="hover:text-white">✕</button>
          </div>
        )}

        <div style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          transformOrigin: '0 0',
          width: contentW, height: contentH,
          position: 'relative', transition: panning || dragNodeId ? 'none' : 'transform 0.15s ease-out'
        }}>
          {/* SVG edges */}
          <svg className="absolute inset-0 pointer-events-none" width={contentW} height={contentH}>
            <defs>
              <marker id="arrow-green" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#22c55e" />
              </marker>
              <marker id="arrow-amber" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#f59e0b" />
              </marker>
              <marker id="arrow-red" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
              </marker>
              <marker id="arrow-back" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#7c3aed" />
              </marker>
            </defs>

            {/* Source → Lead edges (green = bonne zone) */}
            {sourceNodes.map((src, i) => {
              const sx = src.x + offX;
              const sy = src.y + offY + 20;
              const ex = (rootNode?.x || 0) + offX;
              const ey = (rootNode?.y || 0) - NODE_H / 2 + offY;
              return (
                <path key={`src-lead-${i}`}
                  d={`M ${sx} ${sy} C ${sx} ${(sy + ey) / 2}, ${ex} ${(sy + ey) / 2}, ${ex} ${ey}`}
                  stroke="#22c55e" strokeWidth="1.5" fill="none" opacity="0.3" />
              );
            })}

            {/* Source → Lead Hors Zone edges (red = hors zone) */}
            {sourceNodes.length > 0 && siblingNode && sourceNodes.map((src, i) => {
              const sx = src.x + offX;
              const sy = src.y + offY + 20;
              const ex = siblingNode.x + offX;
              const ey = siblingNode.y - NODE_H / 2 + offY;
              return (
                <path key={`src-hz-${i}`}
                  d={`M ${sx} ${sy} C ${sx} ${(sy + ey) / 2}, ${ex} ${(sy + ey) / 2}, ${ex} ${ey}`}
                  stroke="#ef4444" strokeWidth="1" fill="none" opacity="0.15" strokeDasharray="3 3" />
              );
            })}

            {/* DAG edges with color coding */}
            {visibleEdges.map((edge, i) => {
              const fromN = visibleNodes[edge.from];
              const toN = visibleNodes[edge.to];
              if (!fromN || !toN) return null;

              const fx = fromN.x + offX;
              const fy = fromN.y + NODE_H / 2 + offY;
              const tx = toN.x + offX;
              const ty = toN.y - NODE_H / 2 + offY;
              const isBack = toN.y <= fromN.y;
              const ec = getEdgeColor(edge.to);

              // Journey highlight: is this edge part of the journey path?
              const jIds = journeyHighlight?.stageIds || [];
              const jCur = journeyHighlight?.currentStepIdx ?? -1;
              const fromJIdx = jIds.indexOf(edge.from);
              const toJIdx = jIds.indexOf(edge.to);
              // Highlight if both endpoints are in the journey path up to current step, and to comes after from
              const isJourneyEdge = fromJIdx !== -1 && toJIdx !== -1 && toJIdx > fromJIdx && fromJIdx <= jCur;
              const isJourneyDimEdge = !!journeyHighlight && !isJourneyEdge;

              if (isBack) {
                const cx = Math.max(fx, tx) + 60;
                return (
                  <path key={i}
                    d={`M ${fx} ${fy} C ${cx} ${fy}, ${cx} ${ty}, ${tx + NODE_W / 2 + 4} ${toN.y + offY}`}
                    stroke={isJourneyEdge ? '#2dd4bf' : '#7c3aed'} strokeWidth={isJourneyEdge ? 3 : 1.5} fill="none" opacity={isJourneyDimEdge ? 0.08 : isJourneyEdge ? 0.9 : 0.5}
                    strokeDasharray={isJourneyEdge ? undefined : '4 3'} markerEnd="url(#arrow-back)" />
                );
              }
              const midY = (fy + ty) / 2;
              return (
                <React.Fragment key={i}>
                  <path
                    d={`M ${fx} ${fy} C ${fx} ${midY}, ${tx} ${midY}, ${tx} ${ty}`}
                    stroke={isJourneyEdge ? '#2dd4bf' : ec.stroke} strokeWidth={isJourneyEdge ? 3 : 1.5} fill="none" opacity={isJourneyDimEdge ? 0.08 : isJourneyEdge ? 0.9 : ec.opacity}
                    markerEnd={`url(#${isJourneyEdge ? 'arrow-green' : ec.marker})`} />
                  {isJourneyEdge && (
                    <path
                      d={`M ${fx} ${fy} C ${fx} ${midY}, ${tx} ${midY}, ${tx} ${ty}`}
                      stroke="#2dd4bf" strokeWidth="6" fill="none" opacity="0.15" />
                  )}
                </React.Fragment>
              );
            })}
          </svg>

          {/* Source nodes */}
          {sourceNodes.map((src, i) => {
            const isJourneySource = journeyHighlight?.source === src.source;
            const isJourneyDimSource = !!journeyHighlight && !isJourneySource;
            return (
            <div key={src.source} className="absolute" style={{ left: src.x + offX - 55, top: src.y + offY - 18, width: 110 }}
              data-testid={`source-${i}`}>
              <div className={`px-2 py-1.5 border rounded-lg text-center transition-all cursor-pointer
                ${isJourneySource
                  ? 'bg-teal-500/25 border-teal-400 shadow-lg shadow-teal-500/20 scale-110 ring-1 ring-teal-400/40'
                  : isJourneyDimSource
                  ? 'opacity-15 border-slate-700/30 bg-slate-900/50'
                  : traceData?.source === src.source
                  ? 'bg-cyan-500/20 border-cyan-400 shadow-lg shadow-cyan-500/10'
                  : 'bg-indigo-500/10 border-indigo-500/30 hover:bg-indigo-500/20 hover:border-indigo-400'}`}
                onClick={(e) => { e.stopPropagation(); onSourceClick?.(src.source); }}>
                <div className={`text-[10px] font-semibold leading-tight truncate ${isJourneySource ? 'text-teal-300' : 'text-indigo-300'}`}>{src.source}</div>
                <div className={`text-[10px] font-bold mt-0.5 ${isJourneySource ? 'text-teal-400' : 'text-indigo-400'}`}>{src.count}</div>
              </div>
            </div>
            );
          })}

          {/* DAG nodes */}
          {Object.entries(visibleNodes).map(([id, pos]) => {
            const stage = stagesById[id];
            if (!stage) return null;
            const nom = stage.nom || id;
            const color = stage.color || '#6B7280';
            const lc = leadCounts[id] || 0;
            const wf = workflows[id] || {};
            const exits = (wf.exit_triggers || []).filter(e => e.enabled !== false);
            const isTerminal = exits.length === 0;
            const isSelected = selectedStage === id;
            const hasActions = (wf.entry_actions || []).length > 0;
            const hasBackRef = visibleEdges.some(e => e.to === id && visibleNodes[e.from] && visibleNodes[e.from].y >= pos.y && e.from !== id);

            // Trace mode: heatmap
            const traceCount = traceData?.stage_counts?.[id] || 0;
            const traceMax = traceData ? Math.max(1, ...Object.values(traceData.stage_counts || {})) : 0;
            const traceIntensity = traceData ? traceCount / traceMax : 0;
            const isTraceActive = !!traceData;
            const isTraceDimmed = isTraceActive && traceCount === 0;

            // Journey mode: highlight path
            const isJourneyActive = !!journeyHighlight;
            const journeyStepIndex = journeyHighlight?.stageIds?.indexOf(id) ?? -1;
            const isInJourney = journeyStepIndex !== -1;
            const isJourneyCurrent = journeyHighlight?.currentStepIdx !== undefined && journeyStepIndex <= journeyHighlight.currentStepIdx && isInJourney;
            const isJourneyHead = journeyHighlight?.currentStepIdx === journeyStepIndex && isInJourney;
            const isJourneyDimmed = isJourneyActive && !isInJourney;

            const isMultiSelected = selectedNodeIds.has(id);

            return (
              <div key={id} className="absolute" data-testid={`audit-org-${id}`}
                style={{ left: pos.x - NODE_W / 2 + offX, top: pos.y - NODE_H / 2 + offY, width: NODE_W }}>
                <div
                  data-testid={`audit-row-${id}`}
                  className={`relative group cursor-pointer transition-all duration-300 rounded-xl border-2 px-3 py-2 text-center
                    ${isMultiSelected ? 'border-amber-400 bg-amber-500/15 shadow-lg shadow-amber-500/15 ring-2 ring-amber-400/30' :
                    isJourneyDimmed ? 'opacity-15 border-slate-700/30 bg-slate-900/50' :
                      isJourneyHead ? 'border-teal-400 bg-teal-500/20 shadow-lg shadow-teal-500/20 scale-110' :
                      isJourneyCurrent ? 'border-teal-500/50 bg-teal-500/10' :
                      isTraceDimmed ? 'opacity-20 border-slate-700/30 bg-slate-900/50' :
                      isSelected
                      ? 'border-cyan-400 bg-cyan-500/15 shadow-lg shadow-cyan-500/10'
                      : hasBackRef
                        ? 'border-purple-500/40 bg-slate-800/90 hover:border-purple-400 hover:bg-slate-800'
                        : 'border-slate-600/50 bg-slate-800/80 hover:border-slate-500 hover:bg-slate-800'
                    }
                  `}
                  style={isJourneyHead ? {
                    boxShadow: '0 0 25px rgba(20,184,166,0.35)',
                    borderColor: '#2dd4bf'
                  } : isTraceActive && traceCount > 0 ? {
                    boxShadow: `0 0 ${12 + traceIntensity * 20}px rgba(34,211,238,${0.1 + traceIntensity * 0.3})`,
                    borderColor: `rgba(34,211,238,${0.3 + traceIntensity * 0.5})`
                  } : {}}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isTraceActive && traceCount > 0) {
                      onTraceNodeClick?.(id);
                    } else {
                      onSelect(id);
                    }
                    centerOn(id);
                  }}
                >
                  <button data-testid={`audit-config-${id}`}
                    onClick={(e) => { e.stopPropagation(); onSelect(id); }}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-cyan-600 hover:border-cyan-500 z-10"
                    title="Configurer">
                    <Settings className="w-2.5 h-2.5 text-slate-300" />
                  </button>
                  <div
                    onMouseDown={(e) => startNodeDrag(e, id)}
                    className="absolute -top-2 -left-2 w-5 h-5 bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-amber-600 hover:border-amber-500 z-10 cursor-grab active:cursor-grabbing"
                    title="Déplacer"
                    data-testid={`audit-drag-${id}`}
                  >
                    <svg className="w-2.5 h-2.5 text-slate-300" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.5"/><circle cx="11" cy="3" r="1.5"/><circle cx="5" cy="8" r="1.5"/><circle cx="11" cy="8" r="1.5"/><circle cx="5" cy="13" r="1.5"/><circle cx="11" cy="13" r="1.5"/></svg>
                  </div>
                  <div className="absolute top-0 left-3 right-3 h-[3px] rounded-b-full" style={{ backgroundColor: color }} />
                  <div className={`text-[11px] font-semibold leading-tight mt-1 truncate ${isJourneyDimmed ? 'text-slate-700' : isTraceDimmed ? 'text-slate-600' : isJourneyHead ? 'text-teal-200' : 'text-white'}`}>{nom}</div>
                  <div className="flex items-center justify-center gap-1.5 mt-1">
                    {/* Journey mode: show step number */}
                    {isJourneyActive && isInJourney ? (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isJourneyHead ? 'text-teal-100 bg-teal-500/40' : 'text-teal-400 bg-teal-500/15'}`}>
                        #{journeyStepIndex + 1}
                      </span>
                    ) : isTraceActive && traceCount > 0 ? (
                      <span className="text-[11px] font-bold text-cyan-300 bg-cyan-500/20 px-2 py-0.5 rounded-full">{traceCount}</span>
                    ) : (
                      <>
                        {lc > 0 && <span className="text-[10px] font-bold text-cyan-400 bg-cyan-500/10 px-1.5 rounded">{lc}</span>}
                        {hasActions && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="Actions configurees" />}
                        {!hasActions && !isTerminal && <div className="w-1.5 h-1.5 rounded-full bg-slate-600" title="Aucune action" />}
                        {isTerminal && id !== 'lead_monte_escalier' && <span className="text-[8px] text-red-400 font-bold">FIN</span>}
                        {id === 'lead_monte_escalier' && <span className="text-[8px] text-amber-400 font-bold bg-amber-500/15 px-1 rounded">ACORN</span>}
                        {hasBackRef && <span className="text-[8px] text-purple-400 font-medium">ref</span>}
                        {exits.length > 0 && <span className="text-[8px] text-slate-500">{exits.length}</span>}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Orphan nodes — disconnected stages shown on the LEFT, grouped by category */}
          {Object.keys(visibleOrphans).length > 0 && (() => {
            // Build label/color map from categoryConfig
            const catMap = {};
            (categoryConfig || []).forEach(c => { catMap[c.id] = c; });
            const getCatLabel = (cat) => catMap[cat]?.label || (cat === 'sans_categorie' ? 'Autre' : cat);
            const CAT_COLORS_MAP = { blue: '#3b82f6', purple: '#8b5cf6', emerald: '#10b981', slate: '#64748b', red: '#ef4444', amber: '#f59e0b', pink: '#ec4899', orange: '#f97316', cyan: '#06b6d4' };
            const getCatColor = (cat) => CAT_COLORS_MAP[catMap[cat]?.color] || '#6b7280';

            // Category order from config
            const catOrder = (categoryConfig || []).map(c => c.id);
            const sortedCats = Object.keys(orphanCategories || {}).sort((a, b) => {
              const ai = catOrder.indexOf(a); const bi = catOrder.indexOf(b);
              return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            });

            // Compute category header positions from the first orphan in each group
            const catHeaders = sortedCats.map(cat => {
              const firstId = (orphanCategories[cat] || [])[0];
              const firstNode = visibleOrphans[firstId];
              if (!firstNode) return null;
              return { cat, x: firstNode.x, y: firstNode.y - NODE_H / 2 - 22 };
            }).filter(Boolean);

            return (
              <>
                {/* Vertical separator line */}
                <div className="absolute" style={{
                  left: (Object.values(visibleOrphans)[0]?.x || 0) + NODE_W / 2 + offX + 20,
                  top: catHeaders[0] ? catHeaders[0].y + offY - 10 : 0,
                  width: 1,
                  height: Math.max(0, ...Object.values(visibleOrphans).map(n => n.y + NODE_H / 2)) - Math.min(0, ...Object.values(visibleOrphans).map(n => n.y - NODE_H / 2)) + 60,
                  borderLeft: '1px dashed rgba(245, 158, 11, 0.25)'
                }} />

                {/* Orphelins title */}
                <div className="absolute" style={{
                  left: (Object.values(visibleOrphans)[0]?.x || 0) - NODE_W / 2 + offX,
                  top: catHeaders[0] ? catHeaders[0].y + offY - 32 : 0,
                  width: NODE_W
                }}>
                  <div className="text-[9px] uppercase tracking-wider font-bold text-amber-400/70 text-center">
                    Orphelins ({Object.keys(visibleOrphans).length})
                  </div>
                </div>

                {/* Category headers */}
                {catHeaders.map(({ cat, x, y }) => (
                  <div key={`cat-header-${cat}`} className="absolute" style={{
                    left: x - NODE_W / 2 + offX,
                    top: y + offY,
                    width: NODE_W
                  }}>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getCatColor(cat) }} />
                      <span className="text-[9px] font-semibold tracking-wide" style={{ color: getCatColor(cat) }}>
                        {getCatLabel(cat)}
                      </span>
                      <div className="flex-1 h-px" style={{ backgroundColor: `${getCatColor(cat)}30` }} />
                    </div>
                  </div>
                ))}

                {/* Orphan nodes */}
                {Object.entries(visibleOrphans).map(([id, pos]) => {
                  const stage = stagesById[id];
                  if (!stage) return null;
                  const nom = stage.nom || id;
                  const color = stage.color || '#6B7280';
                  const lc = leadCounts[id] || 0;
                  const isSelected = selectedStage === id;
                  const isMultiSelected = selectedNodeIds.has(id);
                  return (
                    <div key={`orphan-${id}`} className="absolute" data-testid={`audit-orphan-${id}`}
                      style={{ left: pos.x - NODE_W / 2 + offX, top: pos.y - NODE_H / 2 + offY, width: NODE_W }}>
                      <div
                        className={`relative group cursor-pointer transition-all duration-200 rounded-xl border-2 border-dashed px-3 py-2 text-center
                          ${isMultiSelected ? 'border-amber-400 bg-amber-500/15 shadow-lg shadow-amber-500/15 ring-2 ring-amber-400/30' :
                          isSelected
                            ? 'border-cyan-400 bg-cyan-500/15 shadow-lg shadow-cyan-500/10'
                            : 'border-amber-500/40 bg-slate-800/60 hover:border-amber-400 hover:bg-slate-800/80'
                          }
                        `}
                        onClick={(e) => { e.stopPropagation(); onSelect(id); }}
                      >
                        <button data-testid={`audit-config-${id}`}
                          onClick={(e) => { e.stopPropagation(); onSelect(id); }}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-cyan-600 hover:border-cyan-500 z-10"
                          title="Configurer">
                          <Settings className="w-2.5 h-2.5 text-slate-300" />
                        </button>
                        <div
                          onMouseDown={(e) => startNodeDrag(e, id)}
                          className="absolute -top-2 -left-2 w-5 h-5 bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-amber-600 hover:border-amber-500 z-10 cursor-grab active:cursor-grabbing"
                          title="Déplacer"
                        >
                          <svg className="w-2.5 h-2.5 text-slate-300" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.5"/><circle cx="11" cy="3" r="1.5"/><circle cx="5" cy="8" r="1.5"/><circle cx="11" cy="8" r="1.5"/><circle cx="5" cy="13" r="1.5"/><circle cx="11" cy="13" r="1.5"/></svg>
                        </div>
                        <div className="absolute top-0 left-3 right-3 h-[3px] rounded-b-full" style={{ backgroundColor: color }} />
                        <div className="text-[11px] font-semibold leading-tight mt-1 truncate text-amber-200">{nom}</div>
                        <div className="flex items-center justify-center gap-1.5 mt-1">
                          {lc > 0 && <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-1.5 rounded">{lc}</span>}
                          <span className="text-[8px] text-amber-500 font-bold">ORPHELIN</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

        </div>
      </div>

      {/* Minimap */}
      {showMinimap && (
        <div className="absolute bottom-3 right-3 bg-slate-900/90 border border-slate-700/50 rounded-xl p-2 backdrop-blur-sm z-20"
          data-testid="dag-minimap" style={{ width: 180 }}>
          <svg viewBox={`0 0 ${contentW} ${contentH}`} width="100%" style={{ maxHeight: 120 }}>
            {/* Mini edges */}
            {visibleEdges.map((edge, i) => {
              const fromN = visibleNodes[edge.from];
              const toN = visibleNodes[edge.to];
              if (!fromN || !toN) return null;
              return (
                <line key={i}
                  x1={fromN.x + offX} y1={fromN.y + offY}
                  x2={toN.x + offX} y2={toN.y + offY}
                  stroke={toN.y <= fromN.y ? "#7c3aed" : "#475569"} strokeWidth="2" opacity="0.4" />
              );
            })}
            {/* Mini nodes */}
            {Object.entries(visibleNodes).map(([id, pos]) => {
              const isSelected = selectedStage === id;
              const color = stagesById[id]?.color || '#6B7280';
              return (
                <rect key={id}
                  x={pos.x - 8 + offX} y={pos.y - 5 + offY} width={16} height={10} rx={2}
                  fill={isSelected ? '#22d3ee' : color} opacity={isSelected ? 1 : 0.6}
                  className="cursor-pointer"
                  onClick={() => { onSelect(id); centerOn(id); }} />
              );
            })}
            {/* Mini orphan nodes */}
            {Object.entries(visibleOrphans).map(([id, pos]) => (
              <rect key={`mini-orphan-${id}`}
                x={pos.x - 8 + offX} y={pos.y - 5 + offY} width={16} height={10} rx={2}
                fill="#f59e0b" opacity={0.5} strokeDasharray="2 1" stroke="#f59e0b" strokeWidth="1"
                className="cursor-pointer"
                onClick={() => { onSelect(id); }} />
            ))}
            {/* Viewport rectangle */}
            {containerRef.current && (() => {
              const rect = containerRef.current.getBoundingClientRect();
              const vx = -pan.x / scale;
              const vy = -pan.y / scale;
              const vw = rect.width / scale;
              const vh = rect.height / scale;
              return (
                <rect x={vx} y={vy} width={vw} height={vh}
                  fill="none" stroke="#22d3ee" strokeWidth="3" opacity="0.5" rx={4} />
              );
            })()}
          </svg>
        </div>
      )}

      {/* Controls overlay */}
      <div className="absolute top-3 right-3 flex items-center gap-1 z-20">
        {/* Group drag buttons */}
        <div
          onMouseDown={(e) => startGroupDrag(e, 'avant_r1')}
          className="px-2 py-1 bg-slate-800/90 hover:bg-blue-700/50 rounded-lg text-[9px] text-slate-400 hover:text-blue-300 backdrop-blur-sm border border-slate-700/50 transition-colors cursor-grab active:cursor-grabbing select-none"
          title="Glisser pour déplacer le groupe Avant R1"
          data-testid="dag-group-avant-r1"
        >
          Avant R1
        </div>
        <div
          onMouseDown={(e) => startGroupDrag(e, 'apres_r1')}
          className="px-2 py-1 bg-slate-800/90 hover:bg-emerald-700/50 rounded-lg text-[9px] text-slate-400 hover:text-emerald-300 backdrop-blur-sm border border-slate-700/50 transition-colors cursor-grab active:cursor-grabbing select-none"
          title="Glisser pour déplacer le groupe Après R1"
          data-testid="dag-group-apres-r1"
        >
          Après R1
        </div>
        <div className="w-px h-4 bg-slate-700/50" />
        {/* Figer / Reset */}
        {hasUnsaved && (
          <button onClick={() => onPositionsChange?.(getAllCurrentPositions())}
            className="px-2 py-1 bg-amber-600/80 hover:bg-amber-500 rounded-lg text-[9px] text-white font-bold backdrop-blur-sm border border-amber-500/50 transition-colors"
            title="Sauvegarder les positions"
            data-testid="dag-freeze"
          >
            Figer
          </button>
        )}
        {frozen && (
          <button onClick={() => { setLocalPositions({}); setHasUnsaved(false); onPositionsChange?.(null); }}
            className="px-2 py-1 bg-slate-800/90 hover:bg-red-700/50 rounded-lg text-[9px] text-slate-400 hover:text-red-300 backdrop-blur-sm border border-slate-700/50 transition-colors"
            title="Revenir au layout automatique"
            data-testid="dag-reset-layout"
          >
            Reset
          </button>
        )}
        <div className="w-px h-4 bg-slate-700/50" />
        <button onClick={fitToScreen} data-testid="dag-fit"
          className="p-1.5 bg-slate-800/90 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white backdrop-blur-sm border border-slate-700/50 transition-colors"
          title="Tout voir">
          <Crosshair className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => rootNode && centerOn(rootId)} data-testid="dag-center-lead"
          className="p-1.5 bg-slate-800/90 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white backdrop-blur-sm border border-slate-700/50 transition-colors"
          title="Centrer sur Lead">
          <Layers className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setShowMinimap(p => !p)} data-testid="dag-toggle-minimap"
          className={`p-1.5 rounded-lg backdrop-blur-sm border transition-colors ${showMinimap ? 'bg-cyan-600/30 border-cyan-500/50 text-cyan-400' : 'bg-slate-800/90 border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700'}`}
          title="Minimap">
          <Map className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-slate-700/50" />
        <div className="flex items-center gap-1.5 bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-lg px-2 py-1">
          <button onClick={() => setScale(s => Math.max(0.15, s * 0.85))} className="text-slate-400 hover:text-white">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <input
            type="range"
            min="15"
            max="300"
            value={Math.round(scale * 100)}
            onChange={e => setScale(parseInt(e.target.value) / 100)}
            className="w-20 h-1 accent-indigo-500 cursor-pointer"
            data-testid="dag-zoom-slider"
          />
          <button onClick={() => setScale(s => Math.min(3, s * 1.15))} className="text-slate-400 hover:text-white">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs text-slate-500 font-mono w-10 text-right">{Math.round(scale * 100)}%</span>
        </div>
      </div>

      {/* Scale indicator + legend */}
      <div className="absolute bottom-3 left-3 flex items-center gap-3 z-20">
        <div className="text-[9px] text-slate-600 bg-slate-900/80 px-2 py-1 rounded-lg border border-slate-700/30">
          {Math.round(scale * 100)}%
        </div>
        <div className="flex items-center gap-2 bg-slate-900/80 px-2 py-1 rounded-lg border border-slate-700/30" data-testid="edge-legend">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-green-500 rounded-full inline-block"></span><span className="text-[8px] text-green-400">Parcours ideal</span></span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-500 rounded-full inline-block"></span><span className="text-[8px] text-amber-400">Intermediaire</span></span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-500 rounded-full inline-block"></span><span className="text-[8px] text-red-400">Perdu/FIN</span></span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-purple-500 rounded-full inline-block" style={{borderTop:'1px dashed #7c3aed'}}></span><span className="text-[8px] text-purple-400">Boucle</span></span>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════
// ACTION FORM (add + edit) with Yaël AI generation
// ════════════════════════════════════════════════════
const ActionForm = ({ tools, stageId, stageName, initialData, onSubmit, onCancel }) => {
  const isEdit = !!initialData;
  const [form, setForm] = useState(initialData || { type: 'notification', target: 'telepro', message: '', delay_minutes: 0, agent_id: '', email_address: '', channel: '' });
  const [generating, setGenerating] = useState(false);
  const [showVars, setShowVars] = useState(false);
  const [crmVars, setCrmVars] = useState([]);
  const textareaRef = useRef(null);

  const actionTools = tools?.action_tools || [];
  const iaAgents = tools?.ia_agents || [];
  const humanRoles = tools?.human_roles || [];
  const emails = tools?.email_addresses || [];

  useEffect(() => {
    af(`${API}/pipeline-workflow/crm-variables`).then(r => r.json()).then(d => setCrmVars(d.variables || [])).catch(() => {});
  }, []);

  const insertVariable = (varKey) => {
    const el = textareaRef.current;
    const tag = `{${varKey}}`;
    if (el) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const newMsg = form.message.substring(0, start) + tag + form.message.substring(end);
      setForm({ ...form, message: newMsg });
      setTimeout(() => { el.focus(); el.setSelectionRange(start + tag.length, start + tag.length); }, 50);
    } else {
      setForm({ ...form, message: form.message + tag });
    }
    setShowVars(false);
  };

  const generateWithYael = async () => {
    setGenerating(true);
    try {
      const res = await af(`${API}/pipeline-workflow/generate-action-content`, {
        method: 'POST',
        body: JSON.stringify({ tool_type: form.type === 'agent_ia' && form.channel ? form.channel : form.type, target: form.target, stage_id: stageId, stage_name: stageName, context: form.message }),
      });
      const data = await res.json();
      if (data.success && data.content) {
        setForm({ ...form, message: data.content });
        toast.success(`Yael a genere le contenu (${data.variables_used?.length || 0} variables)`);
      } else {
        toast.error(data.content || 'Erreur generation');
      }
    } catch (err) {
      toast.error('Erreur: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-slate-800/80 border border-slate-600/40 rounded-xl p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-slate-500 block mb-0.5">Outil</label>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white">
            {actionTools.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500 block mb-0.5">Cible</label>
          <select value={form.target} onChange={e => setForm({ ...form, target: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white">
            {humanRoles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            <option value="client">Client</option>
          </select>
        </div>
      </div>
      {form.type === 'agent_ia' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Agent IA</label>
              <select value={form.agent_id} onChange={e => setForm({ ...form, agent_id: e.target.value })}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white">
                <option value="">-- Choisir --</option>
                {iaAgents.map(a => <option key={a.id} value={a.id}>{a.name} — {a.role}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Canal d'envoi</label>
              <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white">
                <option value="">-- Choisir --</option>
                <option value="whatsapp_baileys">WhatsApp Baileys</option>
                <option value="sms_ringover">SMS Ringover</option>
                <option value="email">Email</option>
                <option value="notification">Notification interne</option>
                <option value="appel_ringover">Appel Ringover</option>
              </select>
            </div>
          </div>
          {form.channel === 'email' && (
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Adresse email</label>
              <select value={form.email_address} onChange={e => setForm({ ...form, email_address: e.target.value })}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white">
                <option value="">-- Choisir --</option>
                {emails.map(e => <option key={e.id} value={e.label}>{e.label}</option>)}
              </select>
            </div>
          )}
        </>
      )}
      {form.type === 'email' && (
        <div>
          <label className="text-[10px] text-slate-500 block mb-0.5">Adresse email</label>
          <select value={form.email_address} onChange={e => setForm({ ...form, email_address: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white">
            <option value="">-- Choisir --</option>
            {emails.map(e => <option key={e.id} value={e.label}>{e.label}</option>)}
          </select>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-0.5">
          <label className="text-[10px] text-slate-500">Message / Description</label>
          <div className="flex items-center gap-1">
            <div className="relative">
              <button onClick={() => setShowVars(!showVars)} data-testid="variables-dropdown-btn"
                className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] text-cyan-400 hover:text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 rounded border border-cyan-500/20 transition-colors">
                <Zap className="w-2.5 h-2.5" /> Variables
              </button>
              {showVars && (
                <div className="absolute right-0 top-6 z-50 w-56 max-h-48 overflow-auto bg-slate-900 border border-slate-600 rounded-xl shadow-2xl p-1.5">
                  {crmVars.map(v => (
                    <button key={v.key} onClick={() => insertVariable(v.key)}
                      className="w-full flex items-center justify-between px-2 py-1 text-[10px] hover:bg-slate-800 rounded-lg transition-colors text-left group">
                      <span className="text-white">{v.label}</span>
                      <span className="text-[8px] text-slate-600 group-hover:text-cyan-400 font-mono">{`{${v.key}}`}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={generateWithYael} disabled={generating} data-testid="generate-yael-btn"
              className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] text-purple-400 hover:text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 rounded border border-purple-500/20 transition-colors disabled:opacity-50">
              {generating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
              {generating ? 'Yael redige...' : 'Generer avec Yael'}
            </button>
          </div>
        </div>
        <textarea ref={textareaRef} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white resize-none focus:border-cyan-500/50 transition-colors" rows={4}
          placeholder="Description de l'action... ou cliquez 'Generer avec Yael'" />
        {form.message && /\{[a-z_]+\}/.test(form.message) && (
          <div className="flex flex-wrap gap-1 mt-1">
            {[...form.message.matchAll(/\{([a-z_]+)\}/g)].map(([, key], i) => {
              const v = crmVars.find(cv => cv.key === key);
              return <span key={i} className="text-[8px] px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 rounded border border-cyan-500/20">{v ? v.label : key}</span>;
            })}
          </div>
        )}
      </div>
      <div>
        <label className="text-[10px] text-slate-500 block mb-0.5">Delai (minutes, 0 = immediat)</label>
        <input type="number" value={form.delay_minutes} onChange={e => setForm({ ...form, delay_minutes: parseInt(e.target.value) || 0 })}
          className="w-24 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white" min="0" />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-2.5 py-1 text-[10px] text-slate-400 hover:text-white">Annuler</button>
        <button onClick={() => {
          if (!form.message && !form.agent_id) { toast.error('Message ou agent requis'); return; }
          onSubmit(isEdit ? { ...form } : { id: `a_${Date.now()}`, ...form, enabled: true });
        }} className="px-2.5 py-1 text-[10px] bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 flex items-center gap-1">
          {isEdit ? <Save className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          {isEdit ? 'Enregistrer' : 'Ajouter'}
        </button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════
// TEST ACTION FORM
// ════════════════════════════════════════════════════
const TestActionForm = ({ action, stageName, onClose }) => {
  const [recipientValue, setRecipientValue] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const channel = action.channel || action.type || 'notification';
  const needsPhone = ['sms_ringover', 'whatsapp_baileys', 'appel_ringover'].includes(channel);
  const needsEmail = channel === 'email';

  const sendTest = async () => {
    if (!recipientValue) { toast.error('Destinataire requis'); return; }
    setTesting(true);
    try {
      const res = await af(`${API}/pipeline-workflow/test-action`, {
        method: 'POST',
        body: JSON.stringify({ action, recipient_type: 'manual', recipient_value: recipientValue, stage_name: stageName }),
      });
      const data = await res.json();
      setResult(data);
      if (data.sent) {
        toast.success(`Message envoye via ${data.preview?.channel || 'canal'}`);
      } else if (data.send_note) {
        toast.info(data.send_note);
      }
    } catch (err) {
      toast.error('Erreur: ' + err.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-amber-400 uppercase">Tester cette action</span>
        <button onClick={onClose} className="p-0.5 hover:bg-slate-800 rounded text-slate-500 hover:text-white"><X className="w-3 h-3" /></button>
      </div>
      <div>
        <label className="text-[10px] text-slate-500 block mb-0.5">
          {needsPhone ? 'Numero de telephone' : needsEmail ? 'Adresse email' : 'Destinataire (nom ou email)'}
        </label>
        <input value={recipientValue} onChange={e => setRecipientValue(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white"
          placeholder={needsPhone ? '06 12 34 56 78' : needsEmail ? 'test@email.com' : 'Nom ou email...'} />
      </div>
      <button onClick={sendTest} disabled={testing} data-testid="send-test-btn"
        className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-semibold rounded-lg transition-colors disabled:opacity-50">
        {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
        {testing ? 'Envoi en cours...' : 'Envoyer le test'}
      </button>
      {result && result.preview && (
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-lg p-2.5 space-y-1.5">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${result.sent ? 'bg-emerald-400' : 'bg-amber-400'}`} />
            <span className={`text-[9px] font-bold uppercase ${result.sent ? 'text-emerald-400' : 'text-amber-400'}`}>
              {result.sent ? 'Envoye' : 'Preview'}
            </span>
          </div>
          <div className="text-[10px] text-white whitespace-pre-wrap leading-relaxed bg-slate-800/60 rounded-lg p-2">{result.preview.resolved_message}</div>
          <div className="flex flex-wrap gap-1.5 text-[8px]">
            <span className="text-slate-500">Canal: <span className="text-amber-400">{result.preview.channel}</span></span>
            <span className="text-slate-500">Dest: <span className="text-amber-400">{result.preview.recipient}</span></span>
            {result.preview.variables_replaced?.length > 0 && (
              <span className="text-slate-500">{result.preview.variables_replaced.length} variables</span>
            )}
          </div>
          {result.send_note && <div className={`text-[9px] italic ${result.sent ? 'text-emerald-400/70' : 'text-slate-600'}`}>{result.send_note}</div>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════
// ADD EXIT TRIGGER FORM
// ════════════════════════════════════════════════════
const AddExitForm = ({ stages, currentStageId, onAdd, onCancel }) => {
  const [form, setForm] = useState({ type: 'manual', target_stage: '', description: '', crm_button_label: '', condition: '' });
  return (
    <div className="bg-slate-800/80 border border-slate-600/40 rounded-xl p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-slate-500 block mb-0.5">Type</label>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white">
            <option value="manual">Manuel (clic CRM)</option>
            <option value="semi_auto">Semi-auto</option>
            <option value="auto">Automatique</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500 block mb-0.5">Etape cible</label>
          <select value={form.target_stage} onChange={e => setForm({ ...form, target_stage: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white">
            <option value="">-- Choisir --</option>
            {stages.filter(s => s.id !== currentStageId).map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[10px] text-slate-500 block mb-0.5">Bouton CRM / Label</label>
        <input value={form.crm_button_label} onChange={e => setForm({ ...form, crm_button_label: e.target.value })}
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white"
          placeholder="Ex: NRP, Pas interesse, RDV pris..." />
      </div>
      <div>
        <label className="text-[10px] text-slate-500 block mb-0.5">Description</label>
        <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white"
          placeholder="Description de la transition" />
      </div>
      <div>
        <label className="text-[10px] text-slate-500 block mb-0.5">Condition (optionnel)</label>
        <input value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-[11px] text-white font-mono"
          placeholder="Ex: nrp_count >= 5" />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-2.5 py-1 text-[10px] text-slate-400 hover:text-white">Annuler</button>
        <button onClick={() => {
          if (!form.target_stage) { toast.error('Etape cible requise'); return; }
          onAdd({ id: `e_${Date.now()}`, source: 'status_change', enabled: true, ...form });
        }} className="px-2.5 py-1 text-[10px] bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 flex items-center gap-1">
          <Plus className="w-3 h-3" /> Ajouter
        </button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════
// COLLAPSIBLE SECTION
// ════════════════════════════════════════════════════
const Section = ({ title, count, color, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-1.5 group">
        <div className="flex items-center gap-1.5">
          {open ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
          <span className={`text-[10px] uppercase tracking-wider font-bold ${color}`}>{title}</span>
          {count !== undefined && <span className="text-[9px] text-slate-600">({count})</span>}
        </div>
      </button>
      {open && <div className="space-y-1.5 mt-1">{children}</div>}
    </div>
  );
};


// ════════════════════════════════════════════════════
// SOCIALAC DETAIL PANEL (right side)
// ════════════════════════════════════════════════════
const SOCIALAC_STATUT_META = {
  nouveaux: { label: 'Nouveaux', color: '#3b82f6' },
  brouillon: { label: 'Brouillon', color: '#64748b' },
  en_cours: { label: 'En cours', color: '#f59e0b' },
  valide: { label: 'Valide', color: '#10b981' },
  refuse: { label: 'Refuse', color: '#ef4444' },
  archive: { label: 'Archive', color: '#8b5cf6' },
};

const SocialACDetailPanel = ({ stageId, socialacStats, dossiers, maxH, onClose }) => {
  const isParent = stageId === 'socialac_parent';
  const statusKey = isParent ? null : stageId.replace('socialac_', '');
  const meta = statusKey ? SOCIALAC_STATUT_META[statusKey] : null;
  const title = isParent ? 'SocialAC — Tous les dossiers' : `SocialAC — ${meta?.label || statusKey}`;
  const color = isParent ? '#ec4899' : (meta?.color || '#64748b');

  const filtered = isParent ? dossiers : dossiers.filter(d => (d.statut || 'nouveaux') === statusKey);

  return (
    <div className="w-80 bg-slate-900 border-l border-slate-700 flex flex-col" style={{ maxHeight: maxH || '100vh' }} data-testid="socialac-detail-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
          <span className="text-sm font-semibold text-white truncate">{title}</span>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors" data-testid="socialac-panel-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 border-b border-slate-800 shrink-0">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{isParent ? 'Total dossiers' : 'Dossiers dans ce statut'}</span>
          <span className="text-lg font-bold" style={{ color }}>{filtered.length}</span>
        </div>
        {isParent && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            {Object.entries(SOCIALAC_STATUT_META).map(([k, m]) => (
              <div key={k} className="text-center">
                <span className="text-sm font-bold" style={{ color: m.color }}>{socialacStats?.byStatut?.[k] || 0}</span>
                <p className="text-[9px] text-slate-500 truncate">{m.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dossier list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {filtered.length === 0 ? (
          <p className="text-xs text-slate-600 text-center py-8">Aucun dossier</p>
        ) : filtered.map(d => (
          <div key={d.socialac_id || d.row_number}
            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg hover:bg-slate-800 transition-colors"
            data-testid={`socialac-panel-dossier-${d.row_number}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-white">{d.nom} {d.prenom}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                style={{ color: SOCIALAC_STATUT_META[d.statut || 'nouveaux']?.color, backgroundColor: `${SOCIALAC_STATUT_META[d.statut || 'nouveaux']?.color || '#666'}15` }}>
                {SOCIALAC_STATUT_META[d.statut || 'nouveaux']?.label || d.statut}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-slate-500">
                {d.sent_at ? new Date(d.sent_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
              </span>
              {d.etape !== null && d.etape !== undefined && (
                <span className="text-[9px] text-slate-600">Etape {d.etape}/3</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ════════════════════════════════════════════════════
// DETAIL PANEL (right side)
// ════════════════════════════════════════════════════
const DetailPanel = ({ stageId, stagesById, workflows, allStages, tools, onSave, maxH, onClose }) => {
  const [showAddAction, setShowAddAction] = useState(false);
  const [showAddExit, setShowAddExit] = useState(false);
  const [editingActionId, setEditingActionId] = useState(null);
  const [testingActionId, setTestingActionId] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [localActions, setLocalActions] = useState([]);
  const [localExits, setLocalExits] = useState([]);

  useEffect(() => {
    const w = workflows[stageId] || {};
    setLocalActions(w.entry_actions || []);
    setLocalExits(w.exit_triggers || []);
    setDirty(false);
    setAdvice(null);
    setShowAddAction(false);
    setShowAddExit(false);
    setEditingActionId(null);
    setTestingActionId(null);
  }, [stageId, workflows]);

  const stage = stagesById[stageId];
  if (!stage) return null;

  const wf = workflows[stageId] || {};
  const entryTriggers = wf.entry_triggers || [];

  const incomingFrom = [];
  Object.entries(workflows).forEach(([sid, swf]) => {
    (swf.exit_triggers || []).forEach(ex => {
      if (ex.target_stage === stageId && ex.enabled !== false) {
        incomingFrom.push({
          from_stage: sid, from_stage_name: stagesById[sid]?.nom || sid,
          from_stage_color: stagesById[sid]?.color || '#6B7280',
          type: ex.type, description: ex.description || '', crm_button_label: ex.crm_button_label || '',
        });
      }
    });
  });

  const handleDeleteAction = (id) => { setLocalActions(prev => prev.filter(a => a.id !== id)); setDirty(true); };
  const handleAddAction = (action) => { setLocalActions(prev => [...prev, action]); setShowAddAction(false); setDirty(true); };
  const handleUpdateAction = (updated) => { setLocalActions(prev => prev.map(a => a.id === editingActionId ? { ...a, ...updated } : a)); setEditingActionId(null); setDirty(true); };
  const handleToggleAction = (id) => { setLocalActions(prev => prev.map(a => a.id === id ? { ...a, enabled: !a.enabled } : a)); setDirty(true); };
  const handleDeleteExit = (id) => { setLocalExits(prev => prev.filter(e => e.id !== id)); setDirty(true); };
  const handleAddExit = (exit) => { setLocalExits(prev => [...prev, exit]); setShowAddExit(false); setDirty(true); };
  const handleToggleExit = (id) => { setLocalExits(prev => prev.map(e => e.id === id ? { ...e, enabled: !e.enabled } : e)); setDirty(true); };

  const handleSave = () => {
    const newWf = { ...wf, entry_actions: localActions, exit_triggers: localExits };
    onSave(stageId, newWf);
    setDirty(false);
    toast.success('Workflow sauvegarde');
  };

  const fetchAdvice = async () => {
    setAdviceLoading(true);
    try {
      const res = await af(`${API}/pipeline-workflow/stage-advice`, {
        method: 'POST',
        body: JSON.stringify({ stage_id: stageId, stage_name: stage.nom || stageId, entry_triggers: entryTriggers, entry_actions: localActions, exit_triggers: localExits, incoming_from: incomingFrom }),
      });
      const data = await res.json();
      setAdvice(data.advice || 'Pas de reponse');
    } catch (err) {
      setAdvice('Erreur: ' + err.message);
    } finally {
      setAdviceLoading(false);
    }
  };

  return (
    <div className="w-[380px] flex-shrink-0 bg-slate-900/95 border border-slate-700/40 rounded-2xl flex flex-col overflow-hidden"
      style={{ maxHeight: maxH }} data-testid="audit-detail-panel">
      <div className="px-4 py-3 border-b border-slate-700/40 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded-full ring-2 ring-white/10" style={{ backgroundColor: stage.color || '#6B7280' }} />
          <h3 className="text-sm font-bold text-white">{stage.nom || stageId}</h3>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button onClick={handleSave} data-testid="audit-save-btn"
              className="flex items-center gap-1 px-2.5 py-1 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-semibold rounded-lg transition-colors">
              <Save className="w-3 h-3" /> Sauvegarder
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-lg text-slate-500 hover:text-white transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4 space-y-4" style={{ overflowX: 'hidden', wordBreak: 'break-word' }}>
        {/* Fiche descriptive */}
        {stage.fiche && (
          <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-3 space-y-2" data-testid="stage-fiche">
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
              <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Responsable</span>
              <span className="text-[10px] text-white font-medium">{stage.fiche.responsable}</span>
              <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Canal</span>
              <span className="text-[10px] text-cyan-300">{stage.fiche.canal}</span>
              <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Objectif</span>
              <span className="text-[10px] text-slate-300">{stage.fiche.objectif}</span>
              <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Delai</span>
              <span className="text-[10px] text-amber-300">{stage.fiche.delai}</span>
            </div>
            {stage.fiche.description_enrichie && (
              <div className="pt-2 mt-2 border-t border-slate-700/30">
                <p className="text-[10px] text-slate-400 leading-relaxed">{stage.fiche.description_enrichie}</p>
              </div>
            )}
          </div>
        )}

        <Section title="Ce qui amene ici" count={incomingFrom.length} color="text-cyan-400">
          {incomingFrom.length === 0 ? (
            <div className="text-[10px] text-slate-500 italic py-1">Etape racine ou orpheline</div>
          ) : incomingFrom.map((inc, i) => (
            <div key={i} className="flex items-center gap-2 py-1 px-2 bg-slate-800/50 rounded-lg">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: inc.from_stage_color }} />
              <span className="text-[10px] text-white font-medium">{inc.from_stage_name}</span>
              <span className={`text-[8px] px-1 py-0.5 rounded border ${typeBadge(inc.type).cls}`}>{typeBadge(inc.type).label}</span>
              {inc.crm_button_label && <span className="text-[9px] text-amber-400/80 bg-amber-500/10 px-1 rounded">CRM: {inc.crm_button_label}</span>}
            </div>
          ))}
        </Section>

        {entryTriggers.length > 0 && (
          <Section title="Declencheurs d'entree" count={entryTriggers.length} color="text-blue-400">
            {entryTriggers.map((t, i) => (
              <div key={i} className="flex items-center gap-1.5 py-1 px-2 bg-slate-800/30 rounded-lg">
                <span className={`text-[8px] px-1 py-0.5 rounded border ${typeBadge(t.type).cls}`}>{t.type}</span>
                <span className="text-[10px] text-slate-300">{t.description || t.source || ''}</span>
              </div>
            ))}
          </Section>
        )}

        <Section title="Quand le lead arrive → faire" count={localActions.length} color="text-emerald-400">
          {localActions.length === 0 && !showAddAction && <div className="text-[10px] text-slate-500 italic py-1">Aucune action automatique</div>}
          {localActions.map((a, i) => (
            <div key={a.id || i}>
              {editingActionId === a.id ? (
                <ActionForm tools={tools} stageId={stageId} stageName={stage.nom || stageId}
                  initialData={a} onSubmit={handleUpdateAction} onCancel={() => setEditingActionId(null)} />
              ) : (
                <>
                  <div className={`flex items-center gap-2 py-1.5 px-2 rounded-lg group transition-all cursor-pointer
                    ${a.enabled !== false ? 'bg-slate-800/50' : 'bg-slate-800/20 opacity-50'}
                    ${testingActionId === a.id ? 'ring-1 ring-amber-500/30' : ''}
                  `}
                    onClick={() => setEditingActionId(a.id)}
                  >
                    {toolIcon(a.type)}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-white font-medium truncate">{a.message || a.description || a.type}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[8px] text-slate-500">{a.target || ''}</span>
                        {a.channel && <span className="text-[8px] text-indigo-400">via {a.channel}</span>}
                        {a.delay_minutes > 0 && <span className="text-[8px] text-slate-500">+{a.delay_minutes}min</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                      <button onClick={() => setTestingActionId(testingActionId === a.id ? null : a.id)}
                        className="p-1 hover:bg-amber-500/20 rounded text-slate-500 hover:text-amber-400" title="Tester">
                        <Zap className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleToggleAction(a.id)} className="p-1 hover:bg-slate-700 rounded">
                        <div className={`w-2 h-2 rounded-full ${a.enabled !== false ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                      </button>
                      <button onClick={() => handleDeleteAction(a.id)} className="p-1 hover:bg-red-500/20 rounded text-slate-500 hover:text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {testingActionId === a.id && (
                    <div className="mt-1">
                      <TestActionForm action={a} stageName={stage.nom || stageId} onClose={() => setTestingActionId(null)} />
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {showAddAction ? (
            <ActionForm tools={tools} stageId={stageId} stageName={stage.nom || stageId} onSubmit={handleAddAction} onCancel={() => setShowAddAction(false)} />
          ) : (
            <button onClick={() => setShowAddAction(true)} data-testid="add-action-btn"
              className="w-full flex items-center justify-center gap-1 py-1.5 border border-dashed border-slate-700 rounded-lg text-[10px] text-slate-500 hover:text-emerald-400 hover:border-emerald-500/30 transition-colors">
              <Plus className="w-3 h-3" /> Ajouter une action
            </button>
          )}
        </Section>

        <Section title="Ce qui fait sortir d'ici" count={localExits.length} color="text-orange-400">
          {localExits.length === 0 && !showAddExit && <div className="text-[10px] text-red-400 italic py-1">FIN — aucune sortie</div>}
          {localExits.map((ex, i) => {
            const targetNom = stagesById[ex.target_stage]?.nom || ex.target_stage;
            const targetColor = stagesById[ex.target_stage]?.color || '#6B7280';
            return (
              <div key={ex.id || i} className={`flex items-center gap-2 py-1.5 px-2 rounded-lg group transition-all ${ex.enabled !== false ? 'bg-slate-800/50' : 'bg-slate-800/20 opacity-50'}`}>
                <span className={`text-[8px] px-1 py-0.5 rounded border flex-shrink-0 ${typeBadge(ex.type).cls}`}>{typeBadge(ex.type).label}</span>
                <ArrowRight className="w-3 h-3 text-slate-600 flex-shrink-0" />
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: targetColor }} />
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] text-white font-medium">{targetNom}</span>
                  {ex.crm_button_label && <span className="text-[9px] text-amber-400/80 ml-1.5 bg-amber-500/10 px-1 rounded">CRM: {ex.crm_button_label}</span>}
                  {ex.description && <div className="text-[9px] text-slate-500 truncate">{ex.description}</div>}
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleToggleExit(ex.id)} className="p-1 hover:bg-slate-700 rounded">
                    <div className={`w-2 h-2 rounded-full ${ex.enabled !== false ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                  </button>
                  <button onClick={() => handleDeleteExit(ex.id)} className="p-1 hover:bg-red-500/20 rounded text-slate-500 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
          {showAddExit ? (
            <AddExitForm stages={allStages} currentStageId={stageId} onAdd={handleAddExit} onCancel={() => setShowAddExit(false)} />
          ) : (
            <button onClick={() => setShowAddExit(true)} data-testid="add-exit-btn"
              className="w-full flex items-center justify-center gap-1 py-1.5 border border-dashed border-slate-700 rounded-lg text-[10px] text-slate-500 hover:text-orange-400 hover:border-orange-500/30 transition-colors">
              <Plus className="w-3 h-3" /> Ajouter une sortie
            </button>
          )}
        </Section>

        <Section title="Avis Claude" color="text-purple-400" defaultOpen={true}>
          {advice ? (
            <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-3">
              <div className="text-[11px] text-slate-300 whitespace-pre-wrap leading-relaxed">{advice}</div>
              <button onClick={fetchAdvice} disabled={adviceLoading}
                className="mt-2 flex items-center gap-1 text-[9px] text-purple-400 hover:text-purple-300">
                <RefreshCw className={`w-3 h-3 ${adviceLoading ? 'animate-spin' : ''}`} /> Rafraichir l'avis
              </button>
            </div>
          ) : (
            <button onClick={fetchAdvice} disabled={adviceLoading} data-testid="ask-claude-btn"
              className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-purple-500/30 rounded-xl text-[11px] text-purple-400 hover:bg-purple-500/5 hover:border-purple-500/50 transition-all disabled:opacity-50">
              {adviceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {adviceLoading ? 'Claude analyse...' : 'Demander l\'avis de Claude'}
            </button>
          )}
        </Section>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════
const WorkflowAudit = () => {
  const [stages, setStages] = useState([]);
  const [workflows, setWorkflows] = useState({});
  const [leadCounts, setLeadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [tools, setTools] = useState(null);
  const [leadSources, setLeadSources] = useState([]);
  const [phaseFilter, setPhaseFilter] = useState(null);
  const [traceData, setTraceData] = useState(null);
  const [traceClients, setTraceClients] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const [categoryConfig, setCategoryConfig] = useState([]);

  // Client journey state
  const [clientJourney, setClientJourney] = useState(null);
  const [journeyHighlight, setJourneyHighlight] = useState(null);

  // DAG positions state (figer/reset)
  const [savedPositions, setSavedPositions] = useState({});
  const [positionsFrozen, setPositionsFrozen] = useState(false);

  // SocialAC stats (for sidebar detail panel when clicking AC stages)
  const [socialacStats, setSocialacStats] = useState(null);
  const [socialacDossiers, setSocialacDossiers] = useState([]);

  const stagesById = {};
  stages.forEach(s => { stagesById[s.id] = s; });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, toolsRes, sourcesRes, posRes, catsRes, socialacRes] = await Promise.all([
        af(`${API}/pipeline-workflow/stages`).then(r => r.json()),
        af(`${API}/pipeline-workflow/available-tools`).then(r => r.json()).catch(() => null),
        af(`${API}/pipeline-workflow/lead-sources`).then(r => r.json()).catch(() => null),
        af(`${API}/pipeline-workflow/dag-positions`).then(r => r.json()).catch(() => ({ positions: {}, frozen: false })),
        af(`${API}/config/pipeline/categories`).then(r => r.json()).catch(() => ({ categories: [] })),
        af(`${API}/socialac/dossiers`).then(r => r.json()).catch(() => ({ dossiers: [] })),
      ]);
      const stagesList = Array.isArray(stagesRes) ? stagesRes : stagesRes.stages || [];
      const wfMap = {};
      const lcMap = {};
      const etapes = stagesList.map(s => {
        const sid = s.stage_id || s.id;
        wfMap[sid] = s.workflow || {};
        lcMap[sid] = s.lead_count || 0;
        return { id: sid, nom: s.stage_name || s.nom || sid, color: s.stage_color || s.color || '#6B7280', categorie: s.categorie || '', actif: s.actif !== false, fiche: s.fiche || null };
      });
      setStages(etapes);
      setWorkflows(wfMap);
      setLeadCounts(lcMap);
      if (toolsRes) setTools(toolsRes);
      if (sourcesRes?.top_sources) setLeadSources(sourcesRes.top_sources.filter(s => s.source && s.source !== 'inconnu'));
      if (posRes?.positions) { setSavedPositions(posRes.positions); setPositionsFrozen(posRes.frozen); }
      if (catsRes?.categories) setCategoryConfig(catsRes.categories);
      // SocialAC stats
      if (socialacRes?.dossiers) {
        const byStatut = {};
        let total = 0;
        socialacRes.dossiers.forEach(d => {
          const s = d.statut || 'nouveaux';
          byStatut[s] = (byStatut[s] || 0) + 1;
          total++;
        });
        setSocialacStats({ byStatut, total });
        setSocialacDossiers(socialacRes.dossiers);
      }
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isFullscreen) return;
    const h = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isFullscreen]);


  // Source trace mode
  const handleSourceClick = async (source) => {
    if (traceData?.source === source) {
      setTraceData(null); setTraceClients(null); return; // toggle off
    }
    setTraceLoading(true);
    try {
      const res = await af(`${API}/pipeline-workflow/source-trace/${encodeURIComponent(source)}`);
      const data = await res.json();
      setTraceData(data);
      setTraceClients(null);
    } catch (err) {
      toast.error('Erreur chargement trace');
    }
    setTraceLoading(false);
  };

  const handleTraceNodeClick = async (stageId) => {
    if (!traceData) return;
    setTraceLoading(true);
    try {
      const res = await af(`${API}/pipeline-workflow/source-trace/${encodeURIComponent(traceData.source)}/clients?stage_id=${stageId}&limit=50`);
      const data = await res.json();
      setTraceClients({ stageId, stageName: stagesById[stageId]?.nom || stageId, clients: data.clients || [] });
    } catch (err) {
      toast.error('Erreur chargement clients');
    }
    setTraceLoading(false);
  };

  const handleSaveWorkflow = async (stageId, newWf) => {
    try {
      await af(`${API}/pipeline-workflow/stages/${stageId}`, {
        method: 'POST',
        body: JSON.stringify({ stage_id: stageId, workflow: newWf }),
      });
      setWorkflows(prev => ({ ...prev, [stageId]: newWf }));
    } catch (err) {
      toast.error('Erreur sauvegarde');
    }
  };

  // Client search & journey
  const handleSelectClient = async (client) => {
    // Clear trace mode when activating journey
    setTraceData(null); setTraceClients(null);
    try {
      const res = await af(`${API}/pipeline-workflow/client-journey/${encodeURIComponent(client.client_id)}`);
      const data = await res.json();
      setClientJourney(data);
      // Map journey stage_ids, keeping only those that exist in DAG for highlighting
      const dagIds = new Set(stages.map(s => s.id));
      const stageIds = (data.journey || []).map(s => s.stage_id).filter(id => dagIds.has(id));
      // Auto-highlight the last step (current position) immediately
      const lastIdx = stageIds.length > 0 ? stageIds.length - 1 : -1;
      setJourneyHighlight({ stageIds, allStageIds: (data.journey || []).map(s => s.stage_id), currentStepIdx: lastIdx, source: data.source || null });
    } catch (err) {
      toast.error('Erreur chargement parcours');
    }
  };

  const handleJourneyStepHighlight = (stepIdx) => {
    if (!clientJourney) return;
    const dagIds = new Set(stages.map(s => s.id));
    const stageIds = (clientJourney.journey || []).map(s => s.stage_id).filter(id => dagIds.has(id));
    // Map stepIdx from full journey to the filtered journey index
    const allIds = (clientJourney.journey || []).map(s => s.stage_id);
    const targetId = allIds[stepIdx];
    const mappedIdx = targetId ? stageIds.indexOf(targetId) : -1;
    setJourneyHighlight({ stageIds, allStageIds: allIds, currentStepIdx: mappedIdx >= 0 ? mappedIdx : stageIds.length - 1, source: clientJourney?.source || null });
  };

  const handleClearJourney = () => {
    setClientJourney(null);
    setJourneyHighlight(null);
  };

  // Save/Reset DAG positions
  const handlePositionsChange = async (positions) => {
    if (positions === null) {
      // Reset
      try {
        await af(`${API}/pipeline-workflow/dag-positions`, { method: 'DELETE' });
        setSavedPositions({});
        setPositionsFrozen(false);
        toast.success('Layout réinitialisé');
      } catch { toast.error('Erreur reset'); }
    } else {
      // Save
      try {
        await af(`${API}/pipeline-workflow/dag-positions`, {
          method: 'POST',
          body: JSON.stringify({ positions }),
        });
        setSavedPositions(positions);
        setPositionsFrozen(true);
        toast.success('Positions figées');
      } catch { toast.error('Erreur sauvegarde'); }
    }
  };

  const totalExits = Object.values(workflows).reduce((sum, wf) => sum + (wf.exit_triggers?.length || 0), 0);
  const deadEnds = stages.filter(s => (workflows[s.id]?.exit_triggers?.length || 0) === 0 && s.id !== 'lead').length;
  const reachable = collectAllIds('lead', workflows);
  reachable.add(SIBLING_ROOT); // lead_hors_zone is a sibling root, not orphan
  const orphans = stages.filter(s => !reachable.has(s.id));
  const maxH = isFullscreen ? 'calc(100vh - 80px)' : '70vh';

  const COLOR_MAP = {
    blue: 'text-blue-400', purple: 'text-purple-400', emerald: 'text-emerald-400',
    red: 'text-red-400', amber: 'text-amber-400', orange: 'text-orange-400',
    cyan: 'text-cyan-400', pink: 'text-pink-400', slate: 'text-slate-400',
  };

  const PHASE_FILTERS = useMemo(() => {
    const cats = [...new Set(stages.map(s => s.categorie).filter(Boolean))];
    // Trier par l'ordre défini dans la config catégories
    const catMap = {};
    categoryConfig.forEach(c => { catMap[c.id] = c; });
    cats.sort((a, b) => (catMap[a]?.ordre || 99) - (catMap[b]?.ordre || 99));
    return [
      { id: null, label: 'Toutes', color: 'text-slate-300' },
      ...cats.map(c => ({
        id: c,
        label: catMap[c]?.label || c,
        color: COLOR_MAP[catMap[c]?.color] || 'text-slate-300'
      }))
    ];
  }, [stages, categoryConfig]);

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-[99999] bg-slate-950 p-4 overflow-hidden' : ''}`} data-testid="workflow-audit">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-white">Audit Workflow</h2>
          <div className="flex items-center gap-1.5 text-[10px]">
            <span className="px-2 py-1 bg-slate-800 rounded-lg text-slate-400">{stages.length} etapes</span>
            <span className="px-2 py-1 bg-slate-800 rounded-lg text-slate-400">{totalExits} transitions</span>
            <span className="px-2 py-1 bg-red-500/15 rounded-lg text-red-400">{deadEnds} fins</span>
            {orphans.length > 0 && <span className="px-2 py-1 bg-amber-500/15 rounded-lg text-amber-400">{orphans.length} orphelins</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Phase filter (e) */}
          <div className="flex items-center bg-slate-800/80 rounded-lg border border-slate-700/50 p-0.5" data-testid="phase-filter">
            {PHASE_FILTERS.map(f => (
              <button key={f.id || 'all'} onClick={() => setPhaseFilter(f.id)}
                data-testid={`phase-${f.id || 'all'}`}
                className={`px-2 py-1 text-[10px] font-medium rounded-md transition-colors ${
                  phaseFilter === f.id ? 'bg-slate-700 text-white' : `${f.color} hover:bg-slate-700/50`
                }`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="w-px h-5 bg-slate-700/50 mx-1" />
          {/* Client search */}
          <ClientSearchBar
            onSelectClient={handleSelectClient}
            activeClientId={clientJourney?.client_id}
            onClear={handleClearJourney}
          />
          <div className="w-px h-5 bg-slate-700/50 mx-1" />
          <button onClick={() => setIsFullscreen(p => !p)} data-testid="audit-fullscreen" className="p-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400">
            {isFullscreen ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}
          </button>
          <button onClick={load} className="p-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex gap-3 flex-1 min-h-0 overflow-hidden">
        {/* DAG Chart panel + trace banner + client drawer */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {/* Trace banner */}
          {traceData && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl px-4 py-2 mb-2 flex items-center justify-between" data-testid="trace-banner">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-sm font-bold text-cyan-300">{traceData.source}</span>
                </div>
                <span className="text-[11px] text-slate-400">{traceData.total} leads</span>
                <span className="text-[11px] text-emerald-400 font-medium">{traceData.conversion_rate}% conversion ({traceData.nb_advanced} avances)</span>
                {traceData.etape_details?.length > 0 && (
                  <span className="text-[10px] text-amber-400">Top: {traceData.etape_details[0]?.etape_nom} ({traceData.etape_details[0]?.count})</span>
                )}
              </div>
              <button onClick={() => { setTraceData(null); setTraceClients(null); }}
                className="p-1 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors" data-testid="trace-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* DAG */}
          <div className={`flex-1 bg-slate-900/50 border border-slate-700/30 rounded-2xl overflow-hidden ${traceClients ? '' : ''}`}
            style={{ maxHeight: clientJourney ? `calc(${maxH} - 160px)` : traceClients ? `calc(${maxH} - 220px)` : maxH, minHeight: '200px' }}>
            {loading || traceLoading ? (
              <div className="flex items-center justify-center py-20 text-slate-500"><RefreshCw className="w-5 h-5 animate-spin mr-2" />Chargement...</div>
            ) : (
              <DagChart
                rootId="lead"
                stagesById={stagesById}
                workflows={workflows}
                leadCounts={leadCounts}
                selectedStage={selectedStage}
                onSelect={setSelectedStage}
                leadSources={leadSources}
                phaseFilter={phaseFilter}
                traceData={traceData}
                onSourceClick={handleSourceClick}
                onTraceNodeClick={handleTraceNodeClick}
                allStageIds={stages.map(s => s.id)}
                journeyHighlight={journeyHighlight}
                savedPositions={savedPositions}
                onPositionsChange={handlePositionsChange}
                frozen={positionsFrozen}
                categoryConfig={categoryConfig}
              />
            )}
          </div>

          {/* Journey timeline (bottom panel) */}
          {clientJourney && (
            <div className="mt-2" data-testid="journey-panel">
              <JourneyTimeline
                journey={clientJourney}
                clientName={`${clientJourney.prenom} ${clientJourney.nom}`}
                onClose={handleClearJourney}
                onStepHighlight={handleJourneyStepHighlight}
              />
            </div>
          )}

          {/* Client drawer (bottom panel) */}
          {traceClients && (
            <div className="mt-2 bg-slate-900/80 border border-slate-700/30 rounded-xl overflow-hidden" data-testid="trace-drawer"
              style={{ maxHeight: '200px' }}>
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700/30 bg-slate-800/50">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-cyan-300">{traceData?.source}</span>
                  <ChevronRight className="w-3 h-3 text-slate-500" />
                  <span className="text-[11px] font-bold text-white">{traceClients.stageName}</span>
                  <span className="text-[10px] text-slate-400 bg-slate-700/50 px-2 py-0.5 rounded-full">{traceClients.clients.length} clients</span>
                </div>
                <button onClick={() => setTraceClients(null)} className="p-1 hover:bg-slate-700 rounded text-slate-500 hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="overflow-auto" style={{ maxHeight: '160px' }}>
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="text-left text-slate-500">
                      <th className="px-4 py-1.5 font-medium">Client</th>
                      <th className="px-4 py-1.5 font-medium">Telephone</th>
                      <th className="px-4 py-1.5 font-medium">Etape</th>
                      <th className="px-4 py-1.5 font-medium">Mis a jour</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traceClients.clients.map((cl, i) => (
                      <tr key={cl.client_id || i} className="border-t border-slate-700/20 hover:bg-slate-800/50 cursor-default" data-testid={`trace-client-${i}`}>
                        <td className="px-4 py-1.5 text-white font-medium">{cl.prenom} {cl.nom}</td>
                        <td className="px-4 py-1.5 text-slate-400">{cl.telephone}</td>
                        <td className="px-4 py-1.5 text-cyan-300">{cl.etape_nom}</td>
                        <td className="px-4 py-1.5 text-slate-500">{cl.updated_at ? new Date(cl.updated_at).toLocaleDateString('fr-FR') : '-'}</td>
                      </tr>
                    ))}
                    {traceClients.clients.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-3 text-center text-slate-500">Aucun client</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Detail/Config panel (always on the right, never affected by trace mode) */}
        {selectedStage && stagesById[selectedStage] && (
          <DetailPanel stageId={selectedStage} stagesById={stagesById} workflows={workflows} allStages={stages}
            tools={tools} onSave={handleSaveWorkflow} maxH={maxH} onClose={() => setSelectedStage(null)} />
        )}
      </div>
    </div>
  );
};

export default WorkflowAudit;
